import { useAxios } from './useFetch';
import { QueryFunctionContext, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { MultiDelete } from 'src/@types/base';
import { FAST_API_URL } from 'src/config';

export function useQueryPost<Output, Input>(
  queryKey: [string, Input],
  endpoint: string,
  config: object,
  url?: string
) {
  const fetch = useAxios<Output, Input>({ endpoint: endpoint, method: 'post', url: url });
  const queryFn = ({ queryKey }: QueryFunctionContext<[string, Input]>) =>
    fetch({ data: queryKey[1] });

  return useQuery({
    queryKey,
    queryFn,
    staleTime: Infinity,
    ...config,
  });
}

interface GenericId {
  id: string;
}

export function useDelete<Input extends MultiDelete, Output, QueryType extends GenericId>(
  queryKey: string[],
  endpoint: string,
  url: string = FAST_API_URL
) {
  const queryClient = useQueryClient();

  const deleteFn = useAxios<Output, Input>({ endpoint: endpoint, method: 'delete', url: url });

  return useMutation({
    mutationFn: (variables) => deleteFn({ data: variables }),
    onMutate: async (data: Input) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKey });

      // Snapshot every entry the optimistic write below reaches. useGet stores a
      // hook's rows under [...queryKey, params], so each params variant holds its
      // own rows under its own key and a single snapshot cannot restore them.
      const previousEntries = queryClient.getQueriesData<Array<QueryType>>({ queryKey });

      // Optimistically update to the new value. An entry still fetching holds no
      // rows, and writing one an empty array cannot be undone, because setQueryData
      // skips an update to undefined; leaving it alone is what keeps it restorable.
      queryClient.setQueriesData<Array<QueryType>>({ queryKey }, (items) => {
        if (!items) return items;
        return items.filter((i) => !data.ids.includes(i.id));
      });

      // Return a context object with the snapshotted values
      return { previousEntries };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, id, context) => {
      context?.previousEntries.forEach(([key, items]) => {
        queryClient.setQueryData(key, items);
      });
    },
    // The rollback writes the rows as they stood when this delete started, so a
    // concurrent delete that succeeded meanwhile comes back with them. Refetching
    // after either outcome is what settles the cache on what the server holds,
    // which useGet's staleTime of Infinity otherwise never does.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function usePut<Input, Output>(
  queryKey: string[],
  endpoint: string,
  config: object = {},
  resourceId?: string,
  url?: string
) {
  const queryClient = useQueryClient();

  if (resourceId) endpoint += '/' + resourceId;
  const putFn = useAxios<Output, Input>({ endpoint: endpoint, method: 'put', url: url });

  return useMutation({
    mutationFn: ({ data, resourceId }: { data: Input; resourceId?: string }) =>
      putFn({ data: data, resourceId: resourceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    ...config,
  });
}

export function usePutAll<Input, Output>(
  queryKey: string[],
  endpoint: string,
  config: object = {}
) {
  const queryClient = useQueryClient();

  const putFn = useAxios<Output, Input>({ endpoint: endpoint, method: 'put' });

  return useMutation({
    mutationFn: (v: Input) => putFn({ data: v }),
    onSuccess: (updatedItems) => {
      queryClient.setQueriesData<Output>({ queryKey }, () => updatedItems);
    },
    ...config,
  });
}

export function usePost<Input, Output>(queryKey: string[], endpoint: string, config: object = {}) {
  const queryClient = useQueryClient();

  const postFn = useAxios<Output, Input>({ endpoint: endpoint, method: 'post' });

  return useMutation({
    mutationFn: ({ data, resourceId }: { data: Input; resourceId?: string }) =>
      postFn({ data: data, resourceId: resourceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
    ...config,
  });
}

export function useGet<Output, Params = undefined>({
  queryKey,
  endpoint,
  config = {},
  resourceId,
  params,
  url = FAST_API_URL,
  requiresAuth = true,
}: {
  queryKey: (string | object)[];
  endpoint: string;
  config?: object;
  resourceId?: string;
  params?: Params;
  url?: string;
  requiresAuth?: boolean;
}) {
  if (resourceId) endpoint += '/' + resourceId;
  const fetchData = useAxios<Output, undefined, typeof params>({
    endpoint: endpoint,
    method: 'get',
    requiresAuth: requiresAuth,
    url: url,
  });

  const formattedQueryKey = params ? [...queryKey, params] : queryKey;

  return useQuery({
    queryKey: formattedQueryKey,
    queryFn: () => fetchData({ params: params }),
    staleTime: Infinity,
    ...config,
  });
}
